/**
 * Builds the text a lesson or drill makes the learner type. A lesson knows only the
 * keys unlocked so far; this turns that key set into drills, syllables and real
 * words — the same idea Keybr uses (fill from the dictionary, fall back to invented
 * combinations when too few real words fit).
 *
 * Keys are US-QWERTY characters. A word "fits" a key set when every keystroke that
 * types it is in the set — computed through the layout's own encoder, so a Bijoy
 * word counts only if every Bijoy key it needs is unlocked.
 */
import { ENGLISH_WORDS } from '../data/englishWords';
import { BANGLA_WORDS } from '../data/banglaWords';
import { BIJOY_CONJUNCTS } from '../data/courses';
import { encodeBijoy, decodeBijoyKeys } from './bijoyLayout';
import { avroToBangla, romanize } from './avroLayout';

const BN_DIGITS = '০১২৩৪৫৬৭৮৯';

/** A small seeded RNG so a lesson's generated text is stable within a session. */
function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a |= 0; a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
const shuffle = (arr, rng) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};
const pick = (arr, n, rng) => shuffle(arr, rng).slice(0, n);

/** US-QWERTY keys a word needs, via the layout encoder. */
export function keysOfWord(layout, word) {
    if (layout.id === 'bijoy') return Array.from(encodeBijoy(layout.normalizeText(word)));
    if (layout.id === 'avro') return Array.from(romanize(word) || ''); // empty = not typeable, never fits
    return Array.from(layout.normalizeText(word).toLowerCase());
}

const fits = (keySet, keys) => keys.length > 0 && keys.every((k) => keySet.has(k));

export function wordsForKeys(layout, allowedKeys, { limit = 60 } = {}) {
    const keySet = new Set(allowedKeys.split(''));
    // Lower-case letters imply their capitals are reachable only when caps is on; the
    // caller passes the already-expanded key set, so just filter.
    const source = layout.script === 'bengali' ? BANGLA_WORDS : ENGLISH_WORDS;
    const out = [];
    for (const word of source) {
        if (fits(keySet, keysOfWord(layout, word))) {
            out.push(word);
            if (out.length >= limit * 3) break;
        }
    }
    return out;
}

/** Two- and three-key drills from the newest keys, e.g. `ffjj fjfj jfjf`. */
function keyDrills(newKeys, allKeys, rng) {
    const fresh = newKeys.length ? newKeys.split('') : allKeys.split('').slice(-4);
    const anchors = allKeys.split('').slice(0, 4);
    const blobs = [];
    fresh.forEach((k) => {
        blobs.push(k + k, k + k);
        anchors.slice(0, 2).forEach((a) => blobs.push(a + k, k + a, a + k + a));
    });
    return pick(blobs, Math.min(blobs.length, 14), rng).join(' ');
}

/** CV / kar syllables a Bijoy beginner can read, built from unlocked keys. */
function bijoySyllables(allowedKeys, rng) {
    const keySet = new Set(allowedKeys.split(''));
    const cons = 'jkhlbmnvrtwy'.split('').filter((k) => keySet.has(k));
    const post = 'fds'.split('').filter((k) => keySet.has(k)); // া ি ু
    const hasLink = keySet.has('g');
    const combos = [];
    cons.forEach((c) => {
        combos.push(c);
        post.forEach((p) => combos.push(p === 'd' ? p + c : c + p));
        if (hasLink) cons.slice(0, 3).forEach((c2) => { if (c2 !== c) combos.push(c + 'g' + c2); });
    });
    // So short a key set that there is barely anything to type (lesson 1) — repeat.
    const want = 16;
    const padded = combos.length >= want ? combos : Array.from({ length: want }, (_, i) => combos[i % combos.length]);
    return pick(padded, want, rng).map((keys) => decodeBijoyKeys(keys)).filter(Boolean).join(' ');
}

/** Avro beginner syllables (কা কি মা …) from the unlocked letters: consonant + vowel keys. */
function avroSyllables(allowedKeys, rng) {
    const keySet = new Set(allowedKeys.split(''));
    const has = (keys) => keys.split('').every((k) => keySet.has(k));
    const cons = ['k', 'm', 't', 'r', 'n', 'l', 'b', 'd', 's', 'p', 'g', 'j', 'c', 'h', 'kh', 'th', 'dh', 'bh', 'T', 'D'].filter(has);
    const vowels = ['a', 'i', 'e', 'u', 'O', 'I', 'U'].filter(has);
    const combos = [];
    cons.forEach((c) => {
        combos.push(c);
        vowels.forEach((v) => combos.push(c + v));
    });
    vowels.forEach((v) => combos.push(v));
    if (!combos.length) return '';
    const want = 16;
    const padded = combos.length >= want ? combos : Array.from({ length: want }, (_, i) => combos[i % combos.length]);
    return pick(padded, want, rng).map((keys) => avroToBangla(keys)).filter(Boolean).join(' ');
}

const toBnNumber = (n) => String(n).replace(/[0-9]/g, (d) => BN_DIGITS[Number(d)]);

/**
 * The practice text for one lesson.
 * @param {Layout} layout
 * @param {object} lesson from courses.js
 * @param {string} cumulativeKeys every key unlocked up to and including this lesson
 * @param {string} prevKeys keys unlocked before it (to spotlight the new ones)
 * @returns {string}
 */
export function buildLessonText(layout, lesson, cumulativeKeys, prevKeys = '', seed = 1) {
    const rng = mulberry32(seed + lesson.id.length * 131);
    const isBn = layout.script === 'bengali';
    const newKeys = cumulativeKeys.split('').filter((k) => !prevKeys.includes(k)).join('');

    if (lesson.numbers) {
        const nums = Array.from({ length: 24 }, () => Math.floor(rng() * 9000) + 100);
        return nums.map((n) => (isBn ? toBnNumber(n) : String(n))).join(' ');
    }
    if (lesson.conjuncts) {
        // Avro: the conjunct drill uses real words that carry one (ক্ত, ন্দ…), since
        // each conjunct is typed by just running its letters together.
        if (layout.id === 'avro') {
            const withConjunct = wordsForKeys(layout, cumulativeKeys, { limit: 200 }).filter((w) => w.includes('্'));
            return pick(withConjunct, Math.min(withConjunct.length, 22), rng).join(' ');
        }
        return pick(BIJOY_CONJUNCTS, 20, rng).join(' ');
    }

    const parts = [];
    if (['home', 'top', 'bottom'].includes(lesson.focus) || lesson.drill) {
        // English drills are the keys themselves (ffjj fjfj); Bijoy keys print Bangla,
        // so its drill has to be real syllables, never the raw f/j characters.
        if (layout.id === 'avro') parts.push(avroSyllables(cumulativeKeys, rng));
        else if (isBn) parts.push(bijoySyllables(cumulativeKeys, rng));
        else parts.push(keyDrills(newKeys, cumulativeKeys, rng));
    }
    const words = wordsForKeys(layout, cumulativeKeys, { limit: 40 });
    if (words.length >= 4) {
        // Lean on the keys this lesson introduces: words that use one of them first,
        // then the rest for review.
        const fresh = new Set(newKeys.split(''));
        const withNew = fresh.size ? wordsForKeys(layout, cumulativeKeys, { limit: 400 }).filter((w) => keysOfWord(layout, w).some((k) => fresh.has(k))) : [];
        const lead = pick(withNew, Math.min(withNew.length, 14), rng);
        const rest = pick(words.filter((w) => !lead.includes(w)), Math.max(0, 22 - lead.length), rng);
        parts.push(shuffle([...lead, ...rest], rng).join(' '));
    } else if (layout.script === 'latin') {
        // Too few real words early on — repeat drills.
        parts.push(keyDrills(newKeys, cumulativeKeys, rng));
    }
    return parts.filter(Boolean).join('   ').trim();
}

/**
 * Adaptive-mode text (Keybr style): bias toward the focus key, fill from real words
 * that fit the included keys, then top up with two-letter blobs of the focus key.
 */
export function buildAdaptiveText(layout, includedKeys, focusKey, { words = 30, seed = 1 } = {}) {
    const rng = mulberry32(seed);
    const pool = wordsForKeys(layout, includedKeys, { limit: 120 });
    const withFocus = pool.filter((w) => keysOfWord(layout, w).includes(focusKey));
    const rest = pool.filter((w) => !withFocus.includes(w));
    const chosen = [];
    for (let i = 0; i < words; i += 1) {
        const useFocus = withFocus.length && rng() < 0.55;
        const bag = useFocus ? withFocus : (rest.length ? rest : pool);
        if (!bag.length) break;
        chosen.push(bag[Math.floor(rng() * bag.length)]);
    }
    return chosen.join(' ');
}
